import { FormControllerProvider } from "@/contexts";
import { Divider } from "@heroui/react";
import { type FC, Fragment, isValidElement, type ReactNode } from "react";
import type {
    FieldValues,
    SubmitErrorHandler,
    SubmitHandler,
    UseFormReturn,
} from "react-hook-form";

interface FormProps {
    id?: string;
    // We use any here since passing the actual type wont bring any value here
    // biome-ignore lint/suspicious/noExplicitAny: <explanation>
    controller: UseFormReturn<any, unknown, unknown>;
    onSubmit?: SubmitHandler<FieldValues>;
    onInvalid?: SubmitErrorHandler<FieldValues>;
    children: ReactNode;
    className?: string;
    dividers?: boolean;
    autoComplete?: string;
}

const FormWrapper = ({
    children,
    dividers,
}: {
    children: ReactNode;
    dividers?: boolean;
}) => (
    <>
        {Array.isArray(children) && dividers
            ? children
                  .filter((child) => {
                      if (!(child && isValidElement(child))) {
                          return false;
                      }

                      if (
                          child.props &&
                          (child.props as Record<string, unknown>)
                              .renderEmpty === true
                      ) {
                          return false;
                      }

                      return true;
                  })
                  .map((child, index) => (
                      // biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
                      <Fragment key={index}>
                          {index > 0 && <Divider className="my-10" />}
                          {child}
                      </Fragment>
                  ))
            : children}
    </>
);

const Form: FC<FormProps> = ({
    id,
    controller,
    onSubmit,
    onInvalid,
    children,
    className = "flex flex-1 flex-col min-w-0",
    dividers = false,
    autoComplete,
}) => {
    const handleSubmit = (event) =>
        onSubmit && controller.handleSubmit(onSubmit, onInvalid)(event);

    return (
        <FormControllerProvider value={controller}>
            <form
                id={id}
                className={className}
                noValidate={true}
                onSubmit={handleSubmit}
                autoComplete={autoComplete}
            >
                <FormWrapper dividers={dividers}>{children}</FormWrapper>
            </form>
        </FormControllerProvider>
    );
};

export { Form, FormWrapper };
